import type { L2Scenario } from "./turo-world-l2-scenarios";

/**
 * Level 3 — ten courtesy words Turo teaches, and the child copies.
 *
 * 🔴 SAME SHAPE AS LEVEL 2, DELIBERATELY. A card, one thing taught, Turo doing
 * it, then silence while the child does it too. No tapping, no camera, no
 * microphone — the two levels differ in CONTENT, not in how a child works them,
 * so a four-year-old who has played L2 already knows what L3 asks of them.
 *
 * ⚠️ THIS REPLACES §4.5's SCENARIO-MATCHING MECHANIC, AND THE COST IS REAL.
 * `TURO_WORLD_L2_L3_PLAN.md` §4.6 built a 24-item bank where a child picks the
 * right greeting from two or three options, with a support ladder on a wrong
 * answer — ordinary graded learning, which §4.1 says L3 is for. Watch-and-copy
 * cannot assess anything, so **L3 no longer tests whether the child has learned
 * the difference**; it teaches and trusts.
 *
 * 📋 THE BANK IS NOT DELETED. `turo-world-l3-scenarios.ts` keeps all 28 items
 * and `TwScenario` still renders them — so the day tapping returns, the graded
 * half is a wiring change rather than a re-authoring. `l3-t3-04` in particular
 * is the item §4.6 calls load-bearing, and losing it would lose the only check
 * that courtesy has not overwritten autonomy.
 *
 * 🔴 THE FOUR TIMES OF DAY ARE THE HARD PART, AND THEY ARE TAUGHT IN ORDER.
 * §4.4: children conflate morning/afternoon/evening/night and then say good
 * night on arrival. Teaching them adjacent and in sequence is what makes the
 * ladder visible.
 */
export const L3_LESSONS: readonly L2Scenario[] = [
  /* ── the words ─────────────────────────────────────────────────────── */
  {
    id: "l3-thankyou",
    card: "card_gift",
    open: ["l3.thanks.open"],
    teach: "l3.teach.thanks",
    poseId: "thank_you",
    practise: "l3.do.thanks",
    close: ["l3.thanks.close"],
  },
  {
    /**
     * 🔴 EVERY SORRY USES THE PAIRED FORM — validator rule 20, §4.4: *"the known
     * failure mode of teaching apology to preschoolers is they learn to PERFORM
     * it — the word becomes a token that ends a conversation rather than fixing
     * anything."* So: "Sorry. Are you okay?", never a bare sorry.
     */
    id: "l3-sorry",
    card: "card_bump",
    open: ["l3.sorry.open"],
    teach: "l3.teach.sorry",
    poseId: "sorry",
    practise: "l3.do.sorry",
    close: ["l3.sorry.close"],
  },
  {
    /** §4.4: sorry = I did something. Excuse me = I need to pass or interrupt. */
    id: "l3-excuseme",
    card: "card_doorway",
    open: ["l3.excuse.open"],
    teach: "l3.teach.excuse",
    poseId: "excuse_me",
    practise: "l3.do.excuse",
    close: ["l3.excuse.close"],
  },
  {
    id: "l3-please",
    card: "card_crayon",
    open: ["l3.please.open"],
    teach: "l3.teach.please",
    poseId: "please_asking",
    practise: "l3.do.please",
    close: ["l3.please.close"],
  },
  {
    /**
     * 🔴 THE BRIDGE FROM LEVEL 2 — §1: *"'No, thank you' is taught explicitly —
     * a refusal that is also courteous, and the bridge between the two levels."*
     * It belongs in the courtesy level precisely because it is BOTH.
     */
    id: "l3-nothankyou",
    card: "card_offered_sweet",
    open: ["l3.nothanks.open"],
    teach: "l3.teach.nothanks",
    poseId: "no_thank_you",
    practise: "l3.do.nothanks",
    close: ["l3.nothanks.close"],
  },

  /* ── all day long ──────────────────────────────────────────────────── */
  {
    id: "l3-morning",
    card: "card_school_gate_morning",
    open: ["l3.morning.open"],
    teach: "l3.teach.morning",
    poseId: "morning_stretch",
    practise: "l3.do.morning",
    close: ["l3.morning.close"],
  },
  {
    id: "l3-afternoon",
    card: "card_school_gate_afternoon",
    open: ["l3.afternoon.open"],
    teach: "l3.teach.afternoon",
    poseId: "afternoon_shade",
    practise: "l3.do.afternoon",
    close: ["l3.afternoon.close"],
  },
  {
    id: "l3-evening",
    card: "card_evening_home",
    open: ["l3.evening.open"],
    teach: "l3.teach.evening",
    poseId: "evening_diya",
    practise: "l3.do.evening",
    close: ["l3.evening.close"],
  },
  {
    /**
     * 🔴 GOOD NIGHT IS A GOODBYE, NOT A HELLO — §4.4, and the close says so
     * outright: *"children conflate all four and then say good night on
     * arrival."*
     */
    id: "l3-night",
    card: "card_bedtime",
    open: ["l3.night.open"],
    teach: "l3.teach.night",
    poseId: "night_sleep",
    practise: "l3.do.night",
    close: ["l3.night.close"],
  },
  {
    /**
     * ⚠️ `wave_bye` DECLARES THE SAME TARGETS AS `wave_hello`, and the close
     * says why: §4.3's core idea is *"hello and goodbye are the SAME gesture —
     * the words differ, not the hands"*, which children find genuinely
     * surprising.
     */
    id: "l3-goodbye",
    card: "card_friend_leaving",
    open: ["l3.bye.open"],
    teach: "l3.teach.bye",
    poseId: "wave_bye",
    practise: "l3.do.bye",
    close: ["l3.bye.same", "l3.bye.close"],
  },
];
